import React, { useState, useEffect, useCallback, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Zap, Mic, ChevronLeft, Timer, XCircle, CheckCircle, Loader, Volume2 } from 'lucide-react';

// --- Tongue Twister Data ---
const TWISTERS = [
    { id: 1, text: "She sells seashells by the seashore.", difficulty: "Easy" },
    { id: 2, text: "Peter Piper picked a peck of pickled peppers.", difficulty: "Easy" },
    { id: 3, text: "Fuzzy Wuzzy was a bear. Fuzzy Wuzzy had no hair.", difficulty: "Medium" },
    { id: 4, text: "I scream, you scream, we all scream for ice cream.", difficulty: "Medium" },
    { id: 5, text: "Unique New York, Unique New York.", difficulty: "Hard" },
    { id: 6, text: "The sixth sick sheik's sixth sheep's sick.", difficulty: "Hard" },
];

const MAX_TIME = 10; 
const SpeechRecognition = window.SpeechRecognition || window.webkitSpeechRecognition;

export default function TwisterTalkGame() {
    const [gameState, setGameState] = useState('uninitialized'); // 'uninitialized', 'ready', 'mic_preparing', 'listening', 'checking', 'result'
    const [currentTwister, setCurrentTwister] = useState(null);
    const [twisterIndex, setTwisterIndex] = useState(0);
    const [timer, setTimer] = useState(MAX_TIME);
    const [liveTranscript, setLiveTranscript] = useState("Click 'Prepare Mic' below.");
    const [micVolume, setMicVolume] = useState(0); // NEW STATE for visual volume feedback
    
    // Refs for Web Audio API (for volume monitoring)
    const audioContextRef = useRef(null);
    const analyserRef = useRef(null);
    const streamRef = useRef(null);
    const volumeDataRef = useRef(null);
    const volumeFrameRef = useRef(0);
    
    // Refs for Web Speech API
    const recognitionRef = useRef(null); 
    const resultReceivedRef = useRef(false);
    const [lastResult, setLastResult] = useState(null); 
    const [score, setScore] = useState(0);

    // --- A. Microphone Preparation (Web Audio API) ---

    const cleanupAudio = useCallback(() => {
        if (volumeFrameRef.current) cancelAnimationFrame(volumeFrameRef.current);
        if (streamRef.current) {
            streamRef.current.getTracks().forEach(track => track.stop());
            streamRef.current = null;
        }
        if (audioContextRef.current) {
            audioContextRef.current.close().catch(e => console.error("Error closing AudioContext:", e));
            audioContextRef.current = null;
        }
        setMicVolume(0);
    }, []);

    const initAudioStream = useCallback(async () => {
        if (gameState === 'mic_preparing') return;

        setGameState('mic_preparing');
        setLiveTranscript("Waiting for microphone permission...");
        
        // 1. Clean up any previous stream
        cleanupAudio();

        try {
            const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
            streamRef.current = stream;

            const audioContext = new (window.AudioContext || window.webkitAudioContext)();
            audioContextRef.current = audioContext;
            await audioContext.resume(); 

            const source = audioContext.createMediaStreamSource(stream);
            const analyser = audioContext.createAnalyser();
            analyser.fftSize = 256; 
            analyserRef.current = analyser;
            volumeDataRef.current = new Uint8Array(analyser.frequencyBinCount);
            
            source.connect(analyser);

            // Start the volume processing loop
            const processVolume = () => {
                const analyser = analyserRef.current;
                const dataArray = volumeDataRef.current;
                if (analyser && dataArray) {
                    analyser.getByteTimeDomainData(dataArray);
                    let sumOfSquares = 0;
                    for (let i = 0; i < dataArray.length; i++) {
                        const value = dataArray[i] - 128; 
                        sumOfSquares += value * value;
                    }
                    const rms = Math.sqrt(sumOfSquares / dataArray.length);
                    const mappedVolume = Math.min(100, Math.floor((rms / 60) * 100));
                    setMicVolume(mappedVolume);
                }
                volumeFrameRef.current = requestAnimationFrame(processVolume);
            };

            volumeFrameRef.current = requestAnimationFrame(processVolume);

            // Mic is ready for speech recognition
            setLiveTranscript("Mic is PREPARED. Click START TALKING.");
            setGameState('ready');

        } catch (error) {
            console.error("Error accessing microphone:", error);
            setLiveTranscript("MIC ERROR: Access denied or failed. Check permissions.");
            setGameState('uninitialized');
        }
    }, [gameState, cleanupAudio]);


    // --- B. Speech Recognition Setup (Runs once) ---
    useEffect(() => {
        if (!SpeechRecognition) return;

        const newRecognition = new SpeechRecognition();
        newRecognition.continuous = false; 
        newRecognition.lang = 'en-US';
        newRecognition.interimResults = true; 
        newRecognition.maxAlternatives = 1;
        recognitionRef.current = newRecognition;

        newRecognition.onresult = (event) => {
            let finalTranscript = '';
            for (let i = event.resultIndex; i < event.results.length; ++i) {
                const transcript = event.results[i][0].transcript;
                if (event.results[i].isFinal) {
                    finalTranscript += transcript;
                } else {
                    setLiveTranscript(transcript); // Interim result update
                }
            }
            
            if (finalTranscript) {
                resultReceivedRef.current = true;
                checkResult(finalTranscript, MAX_TIME - timer);
            }
        };

        newRecognition.onerror = (event) => {
            if (event.error === 'no-speech' || event.error === 'aborted') return;
            checkResult("", MAX_TIME - timer, `Critical Error: ${event.error}`);
        };

        newRecognition.onend = () => {
             if (gameState === 'listening' && timer > 0 && !resultReceivedRef.current) {
                 // Auto-stop occurred, even after preparation. This means the browser is enforcing short sessions.
                 setLiveTranscript("Mic auto-stopped. Speak the twister immediately next time.");
                 setGameState('ready'); 
             }
             resultReceivedRef.current = false;
        };
        
        return () => {
            newRecognition.abort();
            cleanupAudio(); // Ensure cleanup runs on unmount
        };
    }, [timer, gameState, cleanupAudio]);


    // --- C. Game Loop and Flow Control ---

    // Timer Logic
    useEffect(() => {
        let timerInterval;
        if (gameState === 'listening' && timer > 0) {
            timerInterval = setInterval(() => {
                setTimer(prev => prev - 0.1); 
            }, 100);
        } else if (timer <= 0 && gameState === 'listening') {
            recognitionRef.current?.stop();
            checkResult("", MAX_TIME); 
        }
        return () => clearInterval(timerInterval);
    }, [gameState, timer]);


    // Result Checking Logic
    const checkResult = useCallback((spokenText, timeTaken, errorMsg = null) => {
        setGameState('checking'); 
        recognitionRef.current?.abort(); 
        cleanupAudio(); // Stop volume monitor

        const cleanTwister = currentTwister.text.toLowerCase().replace(/[.,!?'"]/g, '');
        const cleanSpoken = spokenText.toLowerCase().replace(/[.,!?'"]/g, '');

        let success = false;
        let message = "Try again! We didn't quite catch that.";
        
        if (errorMsg) {
            message = errorMsg;
        } else if (cleanSpoken.includes(cleanTwister) && cleanSpoken.length >= cleanTwister.length * 0.8) {
            success = true;
            message = `Perfect! You nailed it in ${timeTaken.toFixed(1)} seconds.`;
            setScore(s => s + 1);
        } else if (spokenText.length > 0) {
            message = `Close! We heard: "${spokenText}". You need to match the twister exactly.`;
        } else if (timeTaken >= MAX_TIME) {
            message = "Time ran out! Speak faster and clearer!";
        }

        setLiveTranscript(spokenText); 
        setLastResult({ success, message, time: timeTaken });
        setGameState('result');

    }, [currentTwister, cleanupAudio]);


    // Game Flow Controls
    const startNextTwister = useCallback(() => {
        if (!recognitionRef.current) {
             setLiveTranscript("Speech API not supported.");
             return;
        }

        const nextIndex = twisterIndex % TWISTERS.length;
        setCurrentTwister(TWISTERS[nextIndex]);
        setTwisterIndex(nextIndex + 1);
        setTimer(MAX_TIME);
        setLastResult(null);
        setLiveTranscript("Mic is PREPARED. Click START TALKING.");
        setGameState('ready'); // Start in ready state after mic is prepared

    }, [twisterIndex]);


    const startListening = () => {
        if (gameState !== 'ready' && gameState !== 'result') return;
        
        // This is where we start the recognition *after* the mic stream is known to be active
        setLastResult(null); 
        setTimer(MAX_TIME);
        setLiveTranscript("Mic is LIVE! SPEAK NOW...");
        setGameState('listening');
        
        try {
            recognitionRef.current.abort();
            resultReceivedRef.current = false;

            // Aggressive start
            setTimeout(() => {
                recognitionRef.current.start();
            }, 100); 

        } catch (e) {
            console.error("Failed to start recognition:", e);
            setGameState('ready');
            setLiveTranscript("Error starting mic. Try restarting preparation.");
        }
    };

    const handleRestart = () => {
        setTwisterIndex(0);
        setScore(0);
        setGameState('uninitialized');
        cleanupAudio();
        // The game will re-init when the user clicks 'Prepare Mic'
    };

    // Initialize first twister on mount
    useEffect(() => {
        if (TWISTERS.length > 0 && twisterIndex === 0) {
            setCurrentTwister(TWISTERS[0]);
            setTwisterIndex(1);
        }
    }, [twisterIndex]);


    // --- D. UI Rendering ---

    const renderContent = () => {
        if (!currentTwister) return <p className="text-center text-xl text-gray-500">Loading...</p>;
        const volumePercent = (micVolume / 100) * 100;

        return (
            <>
                {/* Twister Display */}
                <motion.div
                    key={currentTwister.id}
                    initial={{ scale: 0.9, opacity: 0 }}
                    animate={{ scale: 1, opacity: 1 }}
                    className="p-8 bg-pink-100 border-4 border-pink-400 rounded-xl shadow-xl mb-6"
                >
                    <h2 className="text-3xl font-extrabold text-pink-600 mb-4 flex items-center justify-center">
                        Challenge #{twisterIndex} - {currentTwister.difficulty}
                    </h2>
                    <p className="text-4xl md:text-5xl font-bold text-center text-gray-800 leading-snug tracking-tight">
                        "{currentTwister.text}"
                    </p>
                </motion.div>

                {/* Microphone Status/Volume Monitor */}
                <div className="mb-6 p-4 bg-gray-100 border-2 border-gray-300 rounded-lg">
                    <p className="text-sm font-semibold text-gray-600 mb-2 flex items-center gap-1">
                        <Volume2 className="w-4 h-4"/> Mic Volume Check (Speak to see the bar move):
                    </p>
                    <div className="relative h-4 w-full bg-gray-300 rounded-full overflow-hidden">
                        <motion.div
                            className="absolute top-0 left-0 h-full bg-teal-500 shadow-md shadow-teal-300"
                            animate={{ width: `${volumePercent}%` }}
                            transition={{ duration: 0.1 }}
                        />
                    </div>
                </div>
                
                {/* LIVE TRANSCRIPTION FEEDBACK */}
                <div className="text-center mb-6 p-4 bg-teal-50 border-2 border-teal-300 rounded-lg">
                    <p className="text-sm font-semibold text-gray-600 mb-1">Status/What We Hear:</p>
                    {gameState === 'listening' ? (
                        <motion.p 
                            key={liveTranscript}
                            className="text-xl font-mono text-teal-600 font-bold animate-pulse"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            transition={{ duration: 0.2 }}
                        >
                            <span className="text-red-500 text-2xl mr-2">!</span> {liveTranscript} <span className="text-red-500 text-2xl ml-2">!</span>
                        </motion.p>
                    ) : (
                        <p className="text-xl font-mono text-gray-700">{liveTranscript || '---'}</p>
                    )}
                </div>


                {/* Action Button Area */}
                <div className="flex flex-col items-center space-y-4">
                    
                    {/* Timer */}
                    <motion.div 
                        animate={{ scale: gameState === 'listening' ? 1.1 : 1 }}
                        className={`flex items-center space-x-2 text-3xl font-extrabold p-3 rounded-full transition-colors
                                    ${timer <= 3 && gameState === 'listening' ? 'bg-red-500 text-white shadow-red-400' : 'bg-teal-500 text-white shadow-teal-400'} shadow-md`}
                    >
                        <Timer className="w-6 h-6" />
                        <span>{timer.toFixed(1)}s</span>
                    </motion.div>

                    {/* Button based on state */}
                    {gameState === 'uninitialized' ? (
                        <motion.button
                            onClick={initAudioStream}
                            className="flex items-center gap-3 px-8 py-4 bg-blue-500 hover:bg-blue-600 text-white font-bold text-xl rounded-full shadow-lg transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Mic className="w-6 h-6" /> PREPARE MICROPHONE
                        </motion.button>
                    ) : gameState === 'mic_preparing' ? (
                        <div className="flex items-center gap-3 px-8 py-4 bg-yellow-500 text-white font-bold text-xl rounded-full shadow-lg">
                            <Loader className="w-6 h-6 animate-spin" /> Preparing...
                        </div>
                    ) : ['ready', 'result'].includes(gameState) ? (
                        <motion.button
                            onClick={startListening}
                            className="flex items-center gap-3 px-8 py-4 bg-pink-500 hover:bg-pink-600 text-white font-bold text-xl rounded-full shadow-lg transition-colors"
                            whileHover={{ scale: 1.05 }}
                            whileTap={{ scale: 0.95 }}
                        >
                            <Mic className="w-6 h-6" /> START TALKING
                        </motion.button>
                    ) : gameState === 'listening' ? (
                        <motion.button
                            className="flex items-center gap-3 px-8 py-4 bg-red-500 text-white font-bold text-xl rounded-full shadow-lg cursor-not-allowed"
                            animate={{ scale: [1, 1.05, 1], transition: { duration: 1, repeat: Infinity } }}
                        >
                            <Mic className="w-6 h-6 animate-pulse" /> LISTENING...
                        </motion.button>
                    ) : gameState === 'checking' ? (
                        <div className="flex items-center gap-3 px-8 py-4 bg-yellow-500 text-white font-bold text-xl rounded-full shadow-lg">
                            <Loader className="w-6 h-6 animate-spin" /> Checking Vibe...
                        </div>
                    ) : null}
                </div>

                {/* Result Display */}
                <AnimatePresence>
                    {lastResult && gameState === 'result' && (
                        <motion.div
                            initial={{ y: 50, opacity: 0 }}
                            animate={{ y: 0, opacity: 1 }}
                            exit={{ y: 50, opacity: 0 }}
                            className={`mt-8 p-6 rounded-xl shadow-2xl ${lastResult.success ? 'bg-green-100 border-4 border-green-500' : 'bg-red-100 border-4 border-red-500'}`}
                        >
                            <div className="flex items-center justify-center gap-4 mb-3">
                                {lastResult.success ? <CheckCircle className="w-8 h-8 text-green-600" /> : <XCircle className="w-8 h-8 text-red-600" />}
                                <h3 className="text-2xl font-extrabold">{lastResult.success ? "Success!" : "Ouch!"}</h3>
                            </div>
                            <p className="text-center text-lg text-gray-700 font-semibold">{lastResult.message}</p>
                            
                            <div className="flex justify-center space-x-4 mt-6">
                                <button 
                                    onClick={startNextTwister}
                                    className="px-6 py-2 bg-pink-500 hover:bg-pink-600 text-white rounded-full font-bold shadow-md transition-colors"
                                >
                                    Next Twister
                                </button>
                                <button 
                                    onClick={handleRestart}
                                    className="px-6 py-2 bg-teal-500 hover:bg-teal-600 text-white rounded-full font-bold shadow-md transition-colors"
                                >
                                    Restart Game
                                </button>
                            </div>
                        </motion.div>
                    )}
                </AnimatePresence>
            </>
        );
    };


    return (
        <div className="min-h-screen bg-gradient-to-br from-pink-50 to-teal-100 p-6 font-sans text-gray-800">
            <motion.header 
                initial={{ y: -50, opacity: 0 }}
                animate={{ y: 0, opacity: 1 }}
                transition={{ duration: 0.5 }}
                className="max-w-4xl mx-auto mb-10 text-center"
            >
                <h1 className="text-4xl font-extrabold tracking-wider text-teal-600 drop-shadow-md flex items-center justify-center gap-3">
                    <Zap className="w-8 h-8 text-pink-500"/> TWISTER TALK
                </h1>
                <p className="text-gray-600 mt-2 text-lg">
                    Master tricky sounds by speaking the twister clearly and quickly!
                </p>
                <div className="mt-4 text-xl font-bold text-gray-700">
                    Total Score: <span className="text-pink-600">{score}</span>
                </div>
            </motion.header>

            {/* --- Game Container --- */}
            <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl p-6 border-4 border-teal-300">
                
                {renderContent()}

            </div>
            
            {/* Back Button */}
            <div className="mt-10 max-w-4xl mx-auto">
                <button
                    onClick={() => window.history.back()}
                    className="flex items-center gap-2 text-lg text-gray-600 hover:text-gray-800 transition-colors"
                >
                    <ChevronLeft className="w-6 h-6"/> Back to Game Select
                </button>
            </div>
        </div>
    );
}